/** Settings → SIEM Forwarding. */
import { useState } from "react";
import { Panel } from "@/components/surface/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, FlaskConical } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ComingOnline, THead } from "./shared";
import {
  useSiemDestinations, useCreateSiem, useDeleteSiem, useTestSiem,
  type SiemKind,
} from "@/hooks/security/useEdrAdmin";

const KINDS: { value: SiemKind; label: string }[] = [
  { value: "splunk_hec", label: "Splunk HEC" },
  { value: "datadog", label: "Datadog" },
  { value: "sumo", label: "Sumo Logic" },
  { value: "https", label: "Generic HTTPS" },
  { value: "s3", label: "S3 bucket" },
];

export function SiemSection() {
  const q = useSiemDestinations();
  const [addOpen, setAddOpen] = useState(false);
  const unavail = q.data?.kind === "unavailable";
  const dests = q.data?.kind === "ok" ? q.data.data.destinations : [];
  const del = useDeleteSiem();
  const test = useTestSiem();

  return (
    <Panel
      title={`SIEM destinations (${dests.length})`}
      actions={<Button size="sm" disabled={unavail} onClick={() => setAddOpen(true)}><Plus className="h-3.5 w-3.5 mr-1" /> Add destination</Button>}
      loading={q.isLoading}
      empty={!q.isLoading && (unavail || dests.length === 0)}
      emptyTitle={unavail ? "Coming online — Q3 2026" : "No destinations yet"}
      emptyDescription={unavail ? "SIEM forwarding endpoints aren't live yet." : "Forward events to Splunk, Datadog, Sumo, or any HTTPS endpoint."}
    >
      <div className="overflow-x-auto -mx-4 -my-4">
        <table className="w-full text-xs">
          <THead cols={["Name", "Kind", "Endpoint", "Last flush", "Lag", ""]} />
          <tbody>
            {dests.map((d) => (
              <tr key={d.id} className="border-b border-border/60 hover:bg-muted/40">
                <td className="px-3 py-2 font-medium text-foreground">{d.name}</td>
                <td className="px-3 py-2 uppercase text-[10px] text-muted-foreground">{d.kind}</td>
                <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground truncate max-w-[260px]">{d.endpoint}</td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">{d.lastFlushAt ? formatDistanceToNow(new Date(d.lastFlushAt), { addSuffix: true }) : "—"}</td>
                <td className="px-3 py-2 tabular-nums"><span className={d.lagSeconds && d.lagSeconds > 60 ? "text-warning" : "text-success"}>{d.lagSeconds != null ? `${d.lagSeconds}s` : "—"}</span></td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => test.mutate(d.id)} disabled={test.isPending}><FlaskConical className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { if (confirm(`Remove ${d.name}?`)) del.mutate(d.id); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AddSiemDialog open={addOpen} onOpenChange={setAddOpen} />
    </Panel>
  );
}

function AddSiemDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<SiemKind>("splunk_hec");
  const [endpoint, setEndpoint] = useState("");
  const [token, setToken] = useState("");
  const [indexName, setIndexName] = useState("smplify_edr");
  const [format, setFormat] = useState<"ocsf" | "cef" | "json">("ocsf");
  const [forwardAlerts, setFA] = useState(true);
  const [forwardEvents, setFE] = useState(false);
  const [forwardAudit, setFAu] = useState(true);
  const [forwardClassifications, setFC] = useState(false);
  const [cap, setCap] = useState(1000);
  const create = useCreateSiem();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a SIEM destination</DialogTitle>
          <DialogDescription>Push events to a customer SIEM in OCSF, CEF, or raw JSON.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Kind</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as SiemKind)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Endpoint URL</Label><Input value={endpoint} onChange={(e) => setEndpoint(e.target.value)} placeholder="https://splunk.acme.com:8088" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Token</Label><Input type="password" value={token} onChange={(e) => setToken(e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Index (optional)</Label><Input value={indexName} onChange={(e) => setIndexName(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Format</Label>
            <RadioGroup value={format} onValueChange={(v) => setFormat(v as typeof format)} className="flex gap-4">
              {(["ocsf", "cef", "json"] as const).map((f) => (
                <label key={f} className="flex items-center gap-1.5 text-xs">
                  <RadioGroupItem value={f} /> {f === "ocsf" ? "OCSF v1.2 (recommended)" : f === "cef" ? "CEF" : "Raw JSON"}
                </label>
              ))}
            </RadioGroup>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Forward</Label>
            <div className="grid grid-cols-2 gap-1.5 rounded border border-border p-2">
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={forwardAlerts} onCheckedChange={(c) => setFA(!!c)} />Alerts</label>
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={forwardEvents} onCheckedChange={(c) => setFE(!!c)} />Events <span className="text-warning text-[10px]">(volume warning)</span></label>
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={forwardAudit} onCheckedChange={(c) => setFAu(!!c)} />Audit log</label>
              <label className="flex items-center gap-2 text-xs"><Checkbox checked={forwardClassifications} onCheckedChange={(c) => setFC(!!c)} />Agent classifications</label>
            </div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Throughput cap (events/sec)</Label>
            <Input type="number" value={cap} onChange={(e) => setCap(Number(e.target.value))} /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!name || !endpoint || create.isPending} onClick={async () => {
            await create.mutateAsync({
              name, kind, endpoint, token, index: indexName, format,
              forwardAlerts, forwardEvents, forwardAudit, forwardClassifications,
              throughputCapEps: cap,
            });
            onOpenChange(false);
          }}>{create.isPending ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
